import React, { useMemo, useRef, useState } from 'react';
import {
  DataTable,
  DataTablePageEvent,
  DataTableSortEvent,
} from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { LecturesSideBar } from './side-bar/lectures-side-bar';
import { ReportHeader } from '../reports/header/report-header';
import './lectures.page.css';
import {
  getLecturesDataOperation,
  LecturesResType,
} from './operations/get-lectures-data-operation';
import { LecturesSideBarFilterDatamodel } from './side-bar/lectures-side-bar-filter-datamodel';
import dateFormat from '../../utils/date-format';
import dateUtils from '../../utils/date-format';
import {
  getLectureStatusDisplayText,
  LectureStatus,
} from '../../api/types/programs/course/lectures/lecture-status.type';
import Colors from '../../utils/colors';
import { handleExportLecturesToExcel } from './operations/export-lectures-to-excel-operation';

// Mock data for demonstration
const lecturesDataMock = [
  {
    program: 'הנדסת תוכנה',
    startYear: '2023',
    serialNumber: '1',
    course: 'מבוא לתכנות',
    date: '2024-03-20',
    dayOfWeek: 'א׳',
    startTime: '09:00',
    endTime: '10:30',
    classroom: 'כיתה 101',
    status: 'טרם בוצע',
  },
  {
    program: 'מדעי המחשב',
    startYear: '2022',
    serialNumber: '2',
    course: 'אלגוריתמים',
    date: '2024-03-20',
    dayOfWeek: 'א׳',
    startTime: '11:00',
    endTime: '12:30',
    classroom: 'כיתה 102',
    status: 'הושלם בהצלחה',
  },
  {
    program: 'הנדסת תוכנה',
    startYear: '2023',
    serialNumber: '3',
    course: 'תכנות מונחה עצמים',
    date: '2024-03-21',
    dayOfWeek: 'ב׳',
    startTime: '09:00',
    endTime: '10:30',
    classroom: 'כיתה 103',
    status: 'ממתין לאישור',
  },
];

const LecturesPage = () => {
  const [lecturesData, setLecturesData] = useState<LecturesResType[]>([]);
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  // Store current filters for pagination
  const currentFiltersRef = useRef<LecturesSideBarFilterDatamodel | null>(null);

  // Local sort state - applied client-side on the current page (data is lazy-paginated).
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0 | null>(null);

  const handleSort = (event: DataTableSortEvent) => {
    setSortField(event.sortField ?? null);
    setSortOrder((event.sortOrder ?? null) as 1 | -1 | 0 | null);
  };

  const sortedLecturesData = useMemo(() => {
    if (!sortField || !sortOrder) return lecturesData;
    const order = sortOrder;
    const copy = [...lecturesData];

    if (sortField === 'lecturersName') {
      copy.sort((a, b) => {
        const aName = (a.lecturers ?? [])
          .map((l) => `${l.firstName} ${l.lastName}`)
          .join(', ');
        const bName = (b.lecturers ?? [])
          .map((l) => `${l.firstName} ${l.lastName}`)
          .join(', ');
        return aName.localeCompare(bName, 'he') * order;
      });
    } else if (sortField === 'workingStartTime') {
      // Sort by date-only (ignoring time-of-day) so the displayed date column
      // is grouped: all 20/05/26 together, then all 21/05/26, etc.
      const getDateOnlyTimestamp = (
        value: Date | string | undefined,
      ): number => {
        if (!value) return 0;
        const d = new Date(value);
        if (isNaN(d.getTime())) return 0;
        return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      };
      copy.sort((a, b) => {
        const aStart = getDateOnlyTimestamp(a.lecturers?.[0]?.workingStartTime);
        const bStart = getDateOnlyTimestamp(b.lecturers?.[0]?.workingStartTime);
        return (aStart - bStart) * order;
      });
    }

    return copy;
  }, [lecturesData, sortField, sortOrder]);

  const getStatusSeverity = (status: string) => {
    const severityMap: {
      [key: string]: 'success' | 'warning' | 'danger' | 'info';
    } = {
      'טרם בוצע': 'info',
      'הושלם בהצלחה': 'success',
      'ממתין לאישור': 'warning',
    };
    return severityMap[status] || 'info';
  };

  const approveButtonTemplate = (rowData: any) => {
    if (rowData.status === 'ממתין לאישור') {
      return (
        <Button
          severity="success"
          text
          onClick={() => console.log('Approve lecture:', rowData)}
        >
          אשר
        </Button>
      );
    }
    return null;
  };

  // Fetch lectures with current pagination parameters
  const fetchLectures = async (
    filters: LecturesSideBarFilterDatamodel,
    newOffset: number,
    newLimit: number,
  ) => {
    await getLecturesDataOperation(
      filters,
      newOffset,
      newLimit,
      setLoading,
      setLecturesData,
      setTotal,
    );
  };

  const handleSearch = async (filterData: LecturesSideBarFilterDatamodel) => {
    // Store current filters for pagination
    currentFiltersRef.current = filterData;
    // Reset to first page when filters change
    setOffset(0);
    await fetchLectures(filterData, 0, limit);
  };

  // Handle page change from paginator
  const onPageChange = (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);
    if (currentFiltersRef.current) {
      fetchLectures(currentFiltersRef.current, newOffset, newLimit);
    }
  };

  const getLectureStatusStyle = (lectureStatus: LectureStatus) => {
    switch (lectureStatus) {
      case LectureStatus.FINISHED:
        return { color: Colors.blueText, fontWeight: 'bold' };
      case LectureStatus.INIT:
        return { color: Colors.textColor, fontWeight: 'bold' };
      case LectureStatus.START:
        return { color: Colors.warningText, fontWeight: 'bold' };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="מפגשים"
        description="דף זה מציג את כל המפגשים במערכת, כולל פרטים על הקורס, התאריך, השעה והכיתה. ניתן לסנן את המידע באמצעות הסרגל הצדדי ולאשר מפגשים שממתינים לאישור."
        metadata={[
          { label: 'מטרת הדף', value: 'ניהול מפגשים ואישור נוכחות' },
          { label: 'תדירות עדכון', value: 'זמן אמת' },
        ]}
        category="education"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <LecturesSideBar loading={loading} onSearch={handleSearch} />
        </div>

        {/* Table Section */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <label style={{ fontSize: '14px', color: '#495057' }}>
              מספר שורות: {total}
            </label>
            <Button
              style={{
                height: '30px',
                width: '30px',
                backgroundColor: '#10b981',
                borderColor: '#10b981',
              }}
              icon="pi pi-file-excel"
              tooltip="ייצוא לאקסל"
              onClick={() => handleExportLecturesToExcel(lecturesData)}
            />
          </div>
          <DataTable
            value={sortedLecturesData}
            loading={loading}
            rowHover
            rowClassName={() => 'cursor-pointer'}
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            scrollable
            scrollHeight="calc(100vh - 400px)"
            tableStyle={{
              direction: 'rtl',
              height: '100%',
            }}
            style={{ width: '100%' }}
            className="lectures-table"
            paginator
            rows={limit}
            first={offset}
            totalRecords={total}
            onPage={onPageChange}
            lazy={true}
            sortField={sortField ?? undefined}
            sortOrder={sortOrder ?? undefined}
            onSort={handleSort}
            rowsPerPageOptions={[10, 20, 50, 100]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          >
            <Column
              field="branchName"
              header="שלוחות"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />

            <Column
              field="courseName"
              header="קורס"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />

            <Column
              field="additionalCourseName"
              header="שם קורס נוסף"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />

            <Column
              field="abstractProgramName"
              header="תוכניות לימודים"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="programStartYear"
              header="שנת התחלה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />

            <Column
              field="className"
              header="אולם"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />

            <Column
              field="lecturersName"
              header="מרצים"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              sortable
              body={(rowData: LecturesResType) => (
                <div>
                  {' '}
                  {rowData.lecturers
                    .map(
                      (lecturer) =>
                        `${lecturer.firstName} ${lecturer.lastName}`,
                    )
                    .join(', ')}
                </div>
              )}
            />

            <Column
              field="className"
              header="שעות"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: LecturesResType) => (
                <div>
                  {dateFormat.formatTimeRangeBetweenDates(
                    rowData.lecturers[0].workingStartTime,
                    rowData.lecturers[0].workingEndTime,
                  )}
                </div>
              )}
            />

            <Column
              field="studentNumberInCourse"
              header="סך תלמידים משובצים"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />

            <Column
              field="studentAttendance"
              header="סך תלמידים נוכחים"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <label>
                  {rowData.lectureStatus === LectureStatus.INIT
                    ? 'ההרצאה טרם התחילה'
                    : rowData.studentAttendance}
                </label>
              )}
            />

            {/*         <Column
            field="lectureSubject"
            header="נושא המפגש"
            style={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />*/}

            <Column
              field="workingStartTime"
              header="תאריך"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              sortable
              body={(rowData: LecturesResType) => (
                <label>
                  {dateUtils.displayFormatDate(
                    rowData.lecturers[0].workingStartTime,
                  )}
                </label>
              )}
            />

            <Column
              field="lectureSubject"
              header="סטטוס מפגש"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: LecturesResType) => (
                /*<div>{getLectureStatusDisplayText(rowData.lectureStatus)}</div>*/

                <span style={getLectureStatusStyle(rowData.lectureStatus)}>
                  {getLectureStatusDisplayText(rowData.lectureStatus)}
                </span>
              )}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default LecturesPage;
